/**
 * OverlayController — the arbiter.
 *
 * Overlays never decide for themselves whether to render. They claim the
 * screen's single overlay slot with a priority; the highest active claim wins
 * and everything else waits for another visit.
 *
 * Mount the provider once in the app shell:
 *   <OverlayProvider><AppShell /></OverlayProvider>
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { PRIORITY, type OverlayKind } from './overlayCopy';

interface Claim {
  id: string;
  priority: number;
}

interface OverlayContextValue {
  claim: (id: string, priority: number) => void;
  release: (id: string) => void;
  winner: string | null;
}

const OverlayContext = createContext<OverlayContextValue>({
  claim: () => {},
  release: () => {},
  winner: null,
});

export const OverlayProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [claims, setClaims] = useState<Claim[]>([]);

  const claim = useCallback((id: string, priority: number) => {
    setClaims((prev) =>
      prev.some((c) => c.id === id) ? prev : [...prev, { id, priority }]
    );
  }, []);

  const release = useCallback((id: string) => {
    setClaims((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const winner = useMemo(() => {
    if (claims.length === 0) return null;
    return claims.reduce((best, c) => (c.priority > best.priority ? c : best))
      .id;
  }, [claims]);

  const value = useMemo(
    () => ({ claim, release, winner }),
    [claim, release, winner]
  );

  return (
    <OverlayContext.Provider value={value}>{children}</OverlayContext.Provider>
  );
};

/**
 * Returns true when this overlay is allowed to render right now.
 *
 *   const show = useOverlaySlot('welcome_path', 'welcome', !seen && isNewUser);
 *   if (!show) return null;
 *
 * `wants` should already fold in dismissal state and data conditions —
 * the slot only resolves contention between overlays that all want the screen.
 */
export function useOverlaySlot(
  id: string,
  kind: OverlayKind,
  wants: boolean
): boolean {
  const { claim, release, winner } = useContext(OverlayContext);

  useEffect(() => {
    if (wants) {
      claim(id, PRIORITY[kind]);
      return () => release(id);
    }
    release(id);
    return undefined;
  }, [id, kind, wants, claim, release]);

  return wants && winner === id;
}

/**
 * Escape hatch for overlays that must render outside the slot rules —
 * info badges (always on demand) and non-blocking inline banners the user
 * explicitly opened. Use sparingly.
 */
export const useOverlayWinner = (): string | null =>
  useContext(OverlayContext).winner;
